/* eslint-disable @typescript-eslint/no-explicit-any */

import { sanitize } from '@ben/common/dom';
import type { ITheme } from '@ben/orion/utils/theme';
import Checkbox from '@ben/orion/checkbox';
import RadioButton, { RadioButtonVariant } from '@ben/orion/radio-button';
import type { ISlider } from '@ben/orion/slider';
import Slider from '@ben/orion/slider';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type { CustomComponentTypes } from '@ben/shared/constants';
import { camelCase } from 'lodash';
import { memo } from 'react';
import styled, { css, useTheme } from 'styled-components';
import CustomComponent from '../custom-component/custom-component';
import OptionWrapper from './multi-select-option-wrapper';
import type { IMultiSelectOption, TNestedValue } from './multi-select-options';

interface IOption {
  option: IMultiSelectOption;
  disabled?: boolean;
  borderColor?: string;
  selected?: boolean;
  childValues?: Record<string, any>[];
  handleNestedComponentChange: (
    parentKey: string,
    key: string,
    value: TNestedValue,
    type: CustomComponentTypes | string
  ) => void;
  handleChange: (key: string, checked: boolean) => void;
  sliderProps?: ISlider;
  showSlider?: boolean;
  noGap?: boolean;
  disabledSliders: boolean;
  hasSlider: boolean;
  isRadio: boolean;
  benefitId: string | number;
  childError?: Record<string, unknown>;
}
export const MultiSelectOption = ({
  option,
  disabled,
  borderColor,
  selected = false,
  childValues,
  handleNestedComponentChange,
  handleChange,
  sliderProps,
  disabledSliders,
  noGap = false,
  hasSlider,
  isRadio,
  benefitId,
  childError,
}: IOption) => {
  const theme = useTheme();

  const id = `multi-select-option-${option.key}`;

  const hideBottomBorder =
    Boolean(
      selected && option?.subComponents && option.subComponents.length > 0
    ) || Boolean(option?.disabled && option?.reason);

  const sanitizedReason = sanitize(option?.reason || '');

  return (
    <Wrapper>
      <OptionWrapper
        key={option.key}
        title={option.label}
        description={option.description}
        borderColor={borderColor}
        disabled={disabled}
        noGap={noGap}
        hideBottomBorder={hideBottomBorder}
        aria-label={option.label}
        id={id}
      >
        {hasSlider && (
          <SliderWrapper>
            <Slider
              showLabel={false}
              label={`${option.label} Percentage Slider`}
              disabled={disabled}
              {...sliderProps}
              aria-describedby={id}
              isPercentage={true}
            />
          </SliderWrapper>
        )}
        {!isRadio && (
          <Checkbox
            label={option.label}
            value={!disabledSliders && Boolean(selected)}
            onChange={(checked) => handleChange(option.key, checked)}
            showLabel={false}
            disabled={disabled || disabledSliders}
            size="28px"
            aria-describedby={id}
          />
        )}
        {isRadio && (
          <RadioButton
            label={option.label}
            checked={selected}
            value={option.key}
            variant={RadioButtonVariant.Checked}
            showLabel={false}
            onChange={() => handleChange(option.key, true)}
            backgroundColor={theme.palette.neutral['00']}
            disabled={disabled}
            size={28}
            aria-describedby={id}
          />
        )}
      </OptionWrapper>
      {option?.disabled && option?.reason && (
        <ChildContainer $disabled={option.disabled}>
          <Divider />
          <Reason
            variant={TypographyVariant.bodyMd1}
            dangerouslySetInnerHTML={{ __html: sanitizedReason }}
          />
        </ChildContainer>
      )}
      {selected && option?.subComponents && option.subComponents.length > 0 && (
        <ChildContainer>
          {option?.subComponents?.map(({ key, ...subComponent }) => {
            const foundValue = childValues?.find(
              (childValue: Record<string, any>) =>
                childValue.hasOwnProperty(option.key)
            )?.[option.key];

            // Extract error for this specific sub-component from childError
            // Structure: { values: { amount: [{ "0": "error message" }] } }
            const fieldErrors =
              (childError as Record<string, Record<string, unknown>>)?.values?.[
                camelCase(key)
              ] ??
              (childError as Record<string, Record<string, unknown>>)?.values?.[
                key
              ];
            const errorMessage = Array.isArray(fieldErrors)
              ? fieldErrors[0]
              : undefined;

            return (
              <CustomComponent
                key={key}
                {...subComponent}
                benefitId={benefitId}
                onChange={(event: TNestedValue) =>
                  handleNestedComponentChange(
                    option.key,
                    key,
                    event,
                    subComponent.type
                  )
                }
                value={foundValue?.[camelCase(key)] ?? foundValue?.[key]}
                disabled={disabled}
                hasError={!!errorMessage}
                errorMessage={errorMessage}
              />
            );
          })}
        </ChildContainer>
      )}
    </Wrapper>
  );
};

export default memo(MultiSelectOption);

const ChildContainer = styled('div')`
  ${({ theme, $disabled }: { theme: ITheme; $disabled?: boolean }) => css`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    gap: ${theme.spacing(3)};
    padding: ${theme.spacing(3)};
    border: 1px solid ${theme.palette.neutral[60]};
    border-top: none;
    border-radius: 0 0 4px 4px;

    ${$disabled &&
    css`
      padding-top: 0;
      background: ${theme.palette.neutral[40]};
    `}
  `}
`;

const SliderWrapper = styled('div')`
  ${({ theme }) => css`
    min-width: 254px;
    max-width: 254px;
    margin-left: ${theme.spacing(1)};
  `}
`;

const Wrapper = styled('div')`
  display: flex;
  flex-direction: column;
`;

const Divider = styled('div')`
  ${({ theme }) => css`
    width: 100%;
    height: 1px;
    background: ${theme.palette.neutral[60]};
  `}
`;

const Reason = styled(Typography)`
  ${({ theme }) => css`
    & > span {
      ${theme.typography.bodyMd2}
    }
  `}
`;
